import {
  Box,
  Button,
  Card,
  Container,
  Flex,
  Heading,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { subscribeAnalytics, recomputeAnalytics } from '../services/analyticsService'
import { fetchEventById } from '../services/firebaseService'
import { MetricCard } from '../components/analytics/MetricCard'
import { ChallengeBreakdown } from '../components/analytics/ChallengeBreakdown'
import { PeakTimes } from '../components/analytics/PeakTimes'
import { CostCalculator } from '../components/analytics/CostCalculator'
import { toaster } from '../components/ui/toaster'
import { useTranslation } from '../i18n'

export default function AnalyticsPage() {
  const { eventId } = useParams()
  const { t } = useTranslation()

  const [summary, setSummary] = useState(undefined) // undefined = loading, null = no data
  const [eventName, setEventName] = useState(null)
  const [prizeMode, setPrizeMode] = useState(false)
  const [recomputing, setRecomputing] = useState(false)

  useEffect(() => {
    setSummary(undefined)
    const unsub = subscribeAnalytics(
      eventId,
      (data) => setSummary(data),
      (err) => {
        console.error('Analytics subscription error', err)
        setSummary(null)
      }
    )
    return () => unsub()
  }, [eventId])

  useEffect(() => {
    let ignore = false
    fetchEventById(eventId)
      .then((ev) => {
        if (ignore) return
        setEventName(ev?.name || eventId)
        setPrizeMode(Boolean(ev?.challengeMode))
      })
      .catch(() => { if (!ignore) setEventName(eventId) })
    return () => { ignore = true }
  }, [eventId])

  const handleRecompute = async () => {
    setRecomputing(true)
    try {
      const result = await recomputeAnalytics(eventId)
      if (result?.unlimited) {
        toaster.create({ title: t('analytics.recomputeUnlimited'), type: 'success' })
      } else {
        toaster.create({
          title: t('analytics.recomputeSuccess', { remaining: result?.remaining ?? 0 }),
          type: 'success',
        })
      }
    } catch (err) {
      if (err?.code === 'functions/resource-exhausted') {
        toaster.create({ title: t('analytics.recomputeLimitReached'), type: 'warning' })
      } else {
        toaster.create({ title: t('analytics.recomputeError'), type: 'error' })
      }
    } finally {
      setRecomputing(false)
    }
  }

  const lastUpdatedLabel = (() => {
    if (!summary?.computedAt) return t('analytics.notYetComputed')
    try {
      const date = summary.computedAt.toDate()
      return t('analytics.lastUpdated', { date: date.toLocaleString() })
    } catch {
      return t('analytics.notYetComputed')
    }
  })()

  // Derived: submissions per participant
  const submissionsPerParticipant = (() => {
    const s = summary?.counts?.submissions
    const p = summary?.counts?.uniqueParticipants
    if (typeof s !== 'number' || typeof p !== 'number' || p === 0) return null
    return Number((s / p).toFixed(2))
  })()

  // Prize mode is the event's `challengeMode` flag; an event that never ran one
  // has nothing to say about redemptions. The `issued` fallback covers the event
  // fetch failing and an event whose prize mode was switched off after the fact —
  // in both cases the prizes really were handed out.
  const showPrizes = prizeMode || (summary?.prizes?.issued ?? 0) > 0

  return (
    <Container maxW="none" px={0} py={0}>
      <Stack gap={{ base: 4, lg: 6 }}>
        <Flex justify="space-between" align="flex-start" wrap="wrap" gap={4}>
          <Box>
            <Heading size={{ base: 'md', md: 'lg' }} srOnly={{ base: true, lg: false }}>
              {eventName || eventId} — {t('analytics.title')}
            </Heading>
            <Text color="fg.muted" fontSize="sm" mt={1}>
              {lastUpdatedLabel}
            </Text>
          </Box>
          <Button
            colorPalette="brand"
            onClick={handleRecompute}
            loading={recomputing}
            loadingText={t('analytics.recomputing')}
            disabled={recomputing}
            size="sm"
          >
            {t('analytics.recompute')}
          </Button>
        </Flex>

        {summary === undefined && (
          <Flex justify="center" py={10}>
            <Spinner size="xl" />
          </Flex>
        )}

        {summary === null && (
          <Card.Root bg="var(--mc-bg-surface)" border="1px solid" borderColor="var(--mc-border)">
            <Card.Body>
              <Text color="fg.muted" textAlign="center" py={6}>
                {t('analytics.emptyState')}
              </Text>
            </Card.Body>
          </Card.Root>
        )}

        {summary && (
          <Stack gap={6}>
            {/* Key metrics grid */}
            <SimpleGrid columns={{ base: 2, md: 4 }} gap={4}>
              <MetricCard
                label={t('analytics.metricUsableRate')}
                value={summary.rates?.usableContentRate}
                format="percent"
              />
              <MetricCard
                label={t('analytics.metricSubmissions')}
                value={summary.counts?.submissions}
              />
              <MetricCard
                label={t('analytics.metricAccepted')}
                value={summary.counts?.accepted}
              />
              <MetricCard
                label={t('analytics.metricRejected')}
                value={summary.counts?.rejected}
              />
              <MetricCard
                label={t('analytics.metricParticipants')}
                value={summary.counts?.uniqueParticipants}
              />
              <MetricCard
                label={t('analytics.metricSubmissionsPerParticipant')}
                value={submissionsPerParticipant}
              />
              {showPrizes && (
                <MetricCard
                  label={t('analytics.metricRedemptionRate')}
                  value={summary.rates?.redemptionRate}
                  format="percent"
                />
              )}
              <MetricCard
                label={t('analytics.metricAvgDuration')}
                value={summary.duration?.avgOriginalDuration}
                format="seconds"
              />
            </SimpleGrid>

            {/* Peak times */}
            <PeakTimes
              byHourOfDay={summary.timeHistogram?.byHourOfDay}
              eventId={eventId}
            />

            {/* Challenge breakdown */}
            <ChallengeBreakdown byChallenge={summary.byChallenge} />

            {/* Cost calculator */}
            <CostCalculator
              eventId={eventId}
              showRedemption={showPrizes}
              denominators={{
                scans: summary.funnel?.arrivals,
                participants: summary.counts?.uniqueParticipants,
                submissions: summary.counts?.submissions,
                usableClips: summary.counts?.accepted,
                redemptions: summary.prizes?.redeemed,
              }}
            />
          </Stack>
        )}
      </Stack>
    </Container>
  )
}
